import React from "react"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineContext"
import { useIntl } from "react-intl"
import { Box, Button, IconButton, BackIcon, ForwardIcon } from "@carrotfertility/carotene-core"
import { FormRadioGroup, FormSelect } from "@carrotfertility/carotene-core-x"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { NotAllowedWhilePosing } from "#/components/cmd-enrollment/shared/NotAllowedWhilePosing"

/**
 * Stable test-id contract for the benefit-enrollment flow.
 *
 * These ids let the test suite drive and assert the flow by *what a control is*
 * (continue, back, the option whose branch value is X) rather than by translated
 * copy or the underlying widget shape. A widget change must keep the same ids on the
 * functionally-equivalent controls so the journey/decision tests keep passing without edits.
 */
export const ENROLLMENT_TEST_IDS = {
  continue: "enrollment-continue",
  back: "enrollment-back",
  /** Keyed on the option's emitted branch value (e.g. "EMPLOYER", "true", "MEMBER_ONLY"). */
  option: (value: unknown): string => `enrollment-option-${String(value)}`,
  /** Per-screen identity, keyed on the state-machine step name. */
  step: (stepName: string): string => `enrollment-step-${stepName}`
}

interface BenefitEnrollmentFlowModalBackButtonProps {
  handleGoBack?: () => void
}
export function BenefitEnrollmentFlowModalBackButton({
  handleGoBack
}: BenefitEnrollmentFlowModalBackButtonProps): JSX.Element {
  const { gotoPreviousStep } = useStateMachine()
  const intl = useIntl()
  return (
    <IconButton
      size="medium"
      onClick={handleGoBack || gotoPreviousStep}
      aria-label={intl.formatMessage({ defaultMessage: "Back" })}
      color="secondary"
      data-testid={ENROLLMENT_TEST_IDS.back}
    >
      <BackIcon color="secondary" />
    </IconButton>
  )
}

export function BenefitEnrollmentModalContinueButton({ onClick }: { onClick: () => void }): JSX.Element {
  const intl = useIntl()
  const continueText = intl.formatMessage({ defaultMessage: "Continue" })
  const { isPosingAsMember } = useCurrentUser()
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      <Button
        disabled={isPosingAsMember}
        size="medium"
        onClick={onClick}
        aria-label={continueText}
        sx={{ textWrap: "no-wrap" }}
        color="secondary"
        variant="outlined"
        endIcon={<ForwardIcon />}
        data-testid={ENROLLMENT_TEST_IDS.continue}
      >
        {continueText}
      </Button>
      {isPosingAsMember && <NotAllowedWhilePosing />}
    </Box>
  )
}

type BenefitEnrollmentSelectOptionValue = string | number

interface BenefitEnrollmentModalFormSelectProps {
  name: string
  options: { label: string; value: BenefitEnrollmentSelectOptionValue }[]
  defaultValue?: string | number | boolean | null
  "aria-labelledby"?: string
}

/**
 * The enrollment flow's single-select. Built on carotene-core-x `FormSelect` (MUI Select),
 * whose menu is portaled to the body so it is never clipped by, or scrolled inside, the
 * enrollment Dialog (MEM-586).
 *
 * The form value is the selected option's raw `value`, not the option object.
 */
export function BenefitEnrollmentModalFormSelect({
  defaultValue = null,
  name,
  options,
  "aria-labelledby": ariaLabelledBy
}: BenefitEnrollmentModalFormSelectProps): JSX.Element {
  const intl = useIntl()
  // Saved answers come back as strings/booleans; match them to the option's own value type.
  const defaultOption =
    defaultValue !== null ? options.find(({ value }) => String(value) === String(defaultValue)) : undefined
  const placeholder = intl.formatMessage({ defaultMessage: "Select an option" })
  // The closed select shows the chosen option's plain label, or the placeholder while empty
  // (the carotene-core theme sets displayEmpty, so this also runs with no selection).
  const renderValue = (value: unknown): React.ReactNode => {
    const selected = options.find((option) => option.value === value)
    return selected ? (
      selected.label
    ) : (
      <Box component="span" sx={(theme) => ({ color: theme.palette.text.secondary })}>
        {placeholder}
      </Box>
    )
  }

  return (
    <FormSelect
      name={name}
      fullWidth
      // No visible label: the question heading names the select, as it did before the migration.
      labelId={ariaLabelledBy}
      controllerProps={{
        defaultValue: defaultOption?.value ?? "",
        rules: { required: intl.formatMessage({ defaultMessage: "Required" }) }
      }}
      // Each option's label carries the value-keyed test id.
      options={options.map(({ label, value }) => ({
        value,
        label: <span data-testid={ENROLLMENT_TEST_IDS.option(value)}>{label}</span>
      }))}
      renderValue={renderValue}
    />
  )
}

interface BenefitEnrollmentModalFormRadioProps {
  name: string
  options: { label: string; value: string }[]
}
export function BenefitEnrollmentModalFormRadio({ name, options }: BenefitEnrollmentModalFormRadioProps): JSX.Element {
  const intl = useIntl()
  return (
    <FormRadioGroup
      name={name}
      options={options}
      controllerProps={{ rules: { required: intl.formatMessage({ defaultMessage: "Required" }) } }}
    />
  )
}
